<script setup lang="ts">
import { isZeroMoney } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import KpiChangeIndicator from '@/features/dashboard/components/KpiChangeIndicator.vue'
import NetWorthHero from '@/features/dashboard/components/NetWorthHero.vue'
import NetWorthHistoryChart from '@/features/dashboard/components/NetWorthHistoryChart.vue'
import { compareLevelsSamePointLastMonth } from '../internal/month-comparison.ts'
import { TILE_CLASS } from '../internal/tile.ts'
import type { Dashboard, NetWorthSeries } from '../internal/types.ts'

/** The Home screen's one dominant element: net worth, its change, and the full history beneath it. */
const props = defineProps<{
  dashboard: Dashboard
  previousMonth: Dashboard | undefined
  netWorthHistory: NetWorthSeries | undefined
}>()

const { t } = useI18n()

const levels = computed(() =>
  props.previousMonth ? compareLevelsSamePointLastMonth(props.dashboard, props.previousMonth) : undefined,
)
const showLent = computed(() => !isZeroMoney(props.dashboard.totalLent))
const showBorrowed = computed(() => !isZeroMoney(props.dashboard.totalBorrowed))
</script>

<template>
  <section :class="[TILE_CLASS, 'p-[var(--space-page)]']" :aria-label="t('dashboard.netWorth')">
    <div class="flex flex-wrap items-end justify-between gap-[var(--space-group)]">
      <div class="flex min-w-0 flex-col gap-2">
        <NetWorthHero :value="dashboard.netWorth" :label="t('dashboard.netWorth')" />
        <KpiChangeIndicator
          v-if="levels"
          :change="levels.netWorth"
          :caption="t('dashboard.vsSamePointLastMonth')"
        />
      </div>
      <div class="flex min-w-0 flex-col gap-1">
        <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('dashboard.cash') }}
        </p>
        <MoneyAmount :value="dashboard.cash" size="large" />
        <KpiChangeIndicator
          v-if="levels"
          :change="levels.cash"
          :caption="t('dashboard.vsSamePointLastMonth')"
        />
      </div>
    </div>
    <div v-if="showLent || showBorrowed" class="flex flex-wrap gap-[var(--space-group)]">
      <div v-if="showLent" class="flex min-w-0 flex-col gap-1">
        <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('dashboard.totalLent') }}
        </p>
        <MoneyAmount :value="dashboard.totalLent" size="medium" />
      </div>
      <div v-if="showBorrowed" class="flex min-w-0 flex-col gap-1">
        <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('dashboard.totalBorrowed') }}
        </p>
        <MoneyAmount :value="dashboard.totalBorrowed" size="medium" />
      </div>
    </div>
    <NetWorthHistoryChart
      v-if="netWorthHistory"
      :points="netWorthHistory.points"
      :resolution-days="netWorthHistory.resolutionDays"
    />
  </section>
</template>
